import {useCallback} from 'react';

import {useKeyInfoQueryContext} from '../components/MultiRunWorkspace/KeyInfoQueryContext';
import {useSingleRunModeContext} from '../components/MultiRunWorkspace/SingleRunMode';
import {type PanelIsActiveParams} from '../components/PanelBank/types';
import {useSampledHistoryKeys} from '../state/panelGenHistoryKeys/panelGenHistoryKeysStore';
import {panelIsActive} from './panelbank';
import {useIsNgwEnabled} from './rampFeatureFlags/rampFeatureFlags';
import {useKeyTypes} from './runhelpers';

type BoundedParams = 'isSingleRun' | 'historyKeyInfo' | 'keyTypes';
type BoundedPanelIsActiveFn = (
  args: Omit<PanelIsActiveParams, BoundedParams>
) => boolean;

/**
 * Returns a stable callback that delegates to `panelIsActive` with workspace
 * context (history keys, key types, single-run mode) pre-bound.
 */
export function usePanelIsActive(): BoundedPanelIsActiveFn {
  const {isSingleRun} = useSingleRunModeContext();
  const {
    keyInfoQuery: {historyKeyInfo: fullHistoryKeyInfo},
  } = useKeyInfoQueryContext();
  const sampleHistoryKeyInfo = useSampledHistoryKeys();
  const isNgwEnabled = useIsNgwEnabled();
  const historyKeyInfo = isNgwEnabled
    ? sampleHistoryKeyInfo
    : fullHistoryKeyInfo;
  const keyTypes = useKeyTypes(historyKeyInfo.keys);

  return useCallback<BoundedPanelIsActiveFn>(
    args =>
      panelIsActive({
        isSingleRun,
        historyKeyInfo,
        keyTypes,
        ...args,
      }),
    [historyKeyInfo, isSingleRun, keyTypes]
  );
}
